/* =========================================================================
   ClearHom — interactions
   - header scroll state
   - line-by-line text reveal ("curtain" rise from behind overflow:hidden)
   - reviews marquee (auto right→left + pointer drag with inertia)
   - interactive infographic (hover/tap to expand feature points)
   - re-inits cleanly on shopify:section:load
   ========================================================================= */
(function () {
  'use strict';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function escapeHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  // Inline tags preserved through the line split (rich-text emphasis, links).
  var REVEAL_INLINE = { strong: 1, b: 1, em: 1, i: 1, u: 1, mark: 1, a: 1, span: 1 };

  /* ----------------------------- Header ------------------------------- */
  var headerBound = false;
  function initHeader() {
    if (headerBound) return;
    var header = document.querySelector('[data-header]');
    if (!header) return;
    headerBound = true;
    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 12);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* -------------------- Line reveal (curtain) ------------------------- */
  // Split an element's text into visual lines, each wrapped in an
  // overflow:hidden mask with an inner that translates up into view.
  // Idempotent: always rebuilds from the captured original HTML, so it
  // can re-run on resize when wrapping changes. Preserves inline formatting
  // (bold/italic/links from rich-text fields) around each word.
  function splitLines(el) {
    // Capture the original markup once so re-splits rebuild from source.
    if (el.__revealSrc == null) el.__revealSrc = el.innerHTML;

    // Rebuild the author's intent as an ordered list of "segments": each is an
    // array of word tokens (with their inline wrappers) that becomes ONE reveal
    // line — and an EMPTY array is a deliberate blank line. A new segment starts
    // at every <br> and at every block boundary (the <p> paragraphs a rich-text
    // field emits). An empty paragraph (<p></p> or <p><br></p>) yields exactly
    // one blank line, so authors can space guarantee text out on purpose.
    var tmp = document.createElement('div');
    tmp.innerHTML = el.__revealSrc;
    var REVEAL_BLOCK = { p:1, div:1, li:1, h1:1, h2:1, h3:1, h4:1, h5:1, h6:1, blockquote:1 };

    // Collect a node's inline content as lines, splitting on <br>. Inline
    // emphasis/links are carried along each word (open/close wrappers).
    //
    // Al trocear cada nodo de texto por separado y volver a unir las palabras
    // con ' ', aparecían espacios que el autor nunca escribió justo en los
    // bordes de una etiqueta en línea:
    //     ¿<i>Qué es</i> ...              ->  "¿ Qué es ..."
    //     <strong>vapor de agua</strong>, ->  "vapor de agua , sin"
    // Ahora la primera palabra tras un borde se PEGA a la anterior, PERO SOLO
    // cuando hay puntuación de por medio. Es a propósito: parte del contenido
    // ya publicado se escribió sin espacio entre etiquetas ("cortas</strong>de
    // limpieza") y ahí el separador sigue insertándose, como hasta ahora, para
    // no romper textos que hoy se leen bien.
    var ATTACH_LEFT  = /^[,.;:!?%)\]}»…]/;      // signo que se pega a lo anterior
    var ATTACH_RIGHT = /[¿¡(\[{«]$/;             // signo que se pega a lo siguiente
    function inlineLines(node) {
      var lines = [[]];
      var openBoundary = false;   // el último carácter emitido no era espacio
      var lastChar = '';
      (function walk(n, open, close) {
        for (var c = n.firstChild; c; c = c.nextSibling) {
          if (c.nodeType === 3) {
            var text = c.textContent;
            if (text === '') continue;
            var startsSpace = /^\s/.test(text);
            var endsSpace = /\s$/.test(text);
            var parts = text.replace(/\s+/g, ' ').split(' ');
            var first = true;
            for (var i = 0; i < parts.length; i++) {
              if (parts[i] === '') continue;
              var token = open + escapeHtml(parts[i]) + close;
              var bucket = lines[lines.length - 1];
              var noSpace = first && openBoundary && !startsSpace && bucket.length;
              if (noSpace && (ATTACH_LEFT.test(parts[i]) || ATTACH_RIGHT.test(lastChar))) {
                bucket[bucket.length - 1] += token;   // misma palabra, otra etiqueta
              } else {
                bucket.push(token);
              }
              first = false;
            }
            openBoundary = !endsSpace;
            lastChar = text.charAt(text.length - 1);
          } else if (c.nodeType === 1) {
            var tag = c.tagName.toLowerCase();
            if (tag === 'br') { lines.push([]); openBoundary = false; lastChar = ''; continue; }
            if (REVEAL_INLINE[tag]) {
              var attrs = '';
              if (tag === 'a') {
                var href = c.getAttribute('href'); if (href) attrs += ' href="' + escapeAttr(href) + '"';
                var tgt = c.getAttribute('target'); if (tgt) attrs += ' target="' + escapeAttr(tgt) + '"';
                var rel = c.getAttribute('rel'); if (rel) attrs += ' rel="' + escapeAttr(rel) + '"';
              }
              // El subrayado de color (snippet `mark`) lleva su color en el
              // atributo style y su piel en class. Sin esto, al partir el
              // titular en líneas se perdían los dos y el subrayado
              // desaparecía en cuanto la línea se volvía a medir.
              var cls = c.getAttribute('class'); if (cls) attrs += ' class="' + escapeAttr(cls) + '"';
              var stl = c.getAttribute('style'); if (stl) attrs += ' style="' + escapeAttr(stl) + '"';
              walk(c, open + '<' + tag + attrs + '>', '</' + tag + '>' + close);
            } else {
              walk(c, open, close); // unknown inline wrapper: keep text, drop it
            }
          }
        }
      })(node, '', '');
      return lines;
    }

    var segments = [];
    var hasBlock = false;
    for (var b = tmp.firstChild; b; b = b.nextSibling) {
      if (b.nodeType === 1 && REVEAL_BLOCK[b.tagName.toLowerCase()]) { hasBlock = true; break; }
    }
    if (hasBlock) {
      for (var c = tmp.firstChild; c; c = c.nextSibling) {
        if (c.nodeType !== 1 || !REVEAL_BLOCK[c.tagName.toLowerCase()]) continue;
        var ls = inlineLines(c);
        var nonEmpty = ls.some(function (l) { return l.length; });
        if (!nonEmpty) { segments.push([]); continue; }      // empty paragraph -> one blank line
        while (ls.length > 1 && !ls[ls.length - 1].length) ls.pop(); // drop stray trailing <br>
        ls.forEach(function (l) { segments.push(l); });
      }
    } else {
      segments = inlineLines(tmp); // inline field (no block wrappers)
    }

    // Trim blank lines only at the very start/end (stray empty paragraphs are
    // common editor noise); blank lines BETWEEN content are intentional and kept.
    while (segments.length && !segments[0].length) segments.shift();
    while (segments.length && !segments[segments.length - 1].length) segments.pop();

    if (!segments.length) { el.classList.add('is-split'); return; }

    // Capture the element's real rendered width BEFORE we touch its content.
    // A reveal target constrained by a flex/grid sibling (e.g. the guarantee
    // text next to its icon) renders narrower than its own max-width. The
    // inline-block measurement spans below turn the element into a shrink-to-
    // fit box that would otherwise grow to that max-width, so words would be
    // grouped as if the line were wider than it actually renders — producing
    // over-long "lines" that then wrap on screen. Pinning the measured width
    // keeps the line detection honest to the final layout.
    //
    // ARREGLO (v8.2) · "LOS BENEFICIOS DEL HÉROE SE DEFORMAN".
    // Hasta ahora se medía el elemento TAL Y COMO ESTABA, y en un
    // RE-partido eso significaba medirlo YA PARTIDO. El problema: las
    // líneas partidas son bloques, así que un elemento flexible encoge
    // hasta el ancho de su línea más larga. El siguiente partido medía
    // ese ancho ya encogido, agrupaba para una caja más estrecha, el
    // elemento encogía aún más... y así en cada pasada. Como el tema
    // vuelve a partir en `load`, al girar la pantalla y —sobre todo— cada
    // vez que el editor de Shopify repinta la sección, el texto acababa
    // con líneas cortadas a un ancho que no correspondía a nada.
    // Solución: SIEMPRE se restaura el original antes de medir. Así la
    // medida es la del texto natural y el partido es idempotente: partir
    // diez veces da exactamente el mismo resultado que partir una.
    if (el.classList.contains('is-split')) {
      el.innerHTML = el.__revealSrc;
      el.classList.remove('is-split');
    }

    var renderW = el.getBoundingClientRect().width;

    // Si el elemento no tiene ancho real todavía (pestaña oculta, sección
    // recién inyectada por el editor, columna sin asentar), medir aquí
    // daría una palabra por línea. Se deja el texto VISIBLE sin partir y se
    // marca como pendiente: la pasada de reintento lo recogerá cuando ya
    // ocupe su sitio.
    if (renderW < 24) {
      el.__revealPending = true;
      el.classList.add('is-split');
      return;
    }
    el.__revealPending = false;
    el.__revealW = Math.round(renderW);

    var prevWidth = el.style.width;
    var prevBoxSizing = el.style.boxSizing;

    // Detect visual lines. A blank segment becomes one empty line. A non-empty
    // segment is laid out and grouped by offsetTop so long lines still wrap.
    var lines = [];
    segments.forEach(function (seg) {
      if (!seg.length) { lines.push([]); return; } // deliberate blank line

      el.innerHTML = seg.map(function (w) { return '<span class="rw">' + w + '</span>'; }).join(' ');
      // Pin to the captured width (border-box so any padding is preserved).
      el.style.boxSizing = 'border-box';
      el.style.width = renderW + 'px';

      var words = Array.prototype.slice.call(el.querySelectorAll('.rw'));
      // Normalise vertical metrics before measuring. A word wrapped in
      // <strong>/<em> renders a slightly taller inline box; as a plain inline
      // span its offsetTop is reported relative to that taller box, so a bold
      // word landed at an offsetTop BETWEEN two visual lines. The line detector
      // (which groups words by offsetTop) then read that stray value as a new
      // line and isolated every bold word on its own row — the "bold bug".
      // Fix: measure each word as an inline-block box aligned to the top of the
      // line, so offsetTop depends ONLY on which visual line the word sits on.
      words.forEach(function (w) {
        w.style.setProperty('display', 'inline-block', 'important');
        w.style.setProperty('vertical-align', 'top', 'important');
      });

      var top = null, bucket = [];
      words.forEach(function (w) {
        var t = w.offsetTop;
        if (top === null) top = t;
        if (Math.abs(t - top) > 3) { lines.push(bucket); bucket = []; top = t; }
        bucket.push(w.innerHTML);
      });
      if (bucket.length) lines.push(bucket);
    });

    // Release the temporary width pin — the final masked markup flows at the
    // element's natural (constrained) width again.
    el.style.width = prevWidth;
    el.style.boxSizing = prevBoxSizing;

    // Rebuild as masked, translatable lines (formatting preserved). A blank
    // line keeps its height via a non-breaking space so it still occupies a row.
    el.innerHTML = lines.map(function (lineWords, i) {
      return '<span class="reveal-line-mask"><span class="reveal-line-inner" style="--line-index:' +
        i + '">' + (lineWords.length ? lineWords.join(' ') : '&nbsp;') + '</span></span>';
    }).join('');

    el.classList.add('is-split');
  }

  /* ---- Shared collapse reveal -------------------------------------- */
  // For content that lives inside a collapsible panel (FAQ answers, PDP
  // accordion bodies): each block-level line rises with the curtain effect
  // every time the panel opens. Line wrapping depends on width (not height),
  // so splitting works fine even while the panel is collapsed.
  function prepCollapse(rte) {
    var lines = [];
    if (!rte) return lines;
    Array.prototype.slice.call(rte.children).forEach(function (node, i) {
      if (!node.textContent || !node.textContent.trim()) return;
      node.classList.add('reveal-lines');
      node.style.setProperty('--reveal-offset', (i * 0.12) + 's');
      lines.push(node);
    });
    return lines;
  }
  function showCollapse(state) {
    if (!state.split) {
      if (!reduce) state.lines.forEach(splitLines);
      state.split = true;
    }
    if (reduce) {
      state.lines.forEach(function (l) { l.classList.add('is-split', 'is-in'); });
      return;
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        state.lines.forEach(function (l) { l.classList.add('is-in'); });
      });
    });
  }
  function hideCollapse(state) {
    state.lines.forEach(function (l) { l.classList.remove('is-in'); });
  }

  function buildReveals(root) {
    root.querySelectorAll('.reveal-lines').forEach(function (el) {
      // Collapsible content (FAQ answers, PDP accordion bodies) reveals on open
      if (el.closest('[data-faq-body], .pdp-acc__body')) return;
      splitLines(el);
    });
  }

  var io = null;
  function getIO() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          en.target.classList.add('is-in');
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
    return io;
  }

  function observeReveals(root) {
    var o = getIO();
    root.querySelectorAll('.reveal-lines, [data-reveal]').forEach(function (el) {
      if (el.dataset.observed === '1') return;
      // Collapsible content reveals on open, not on scroll
      if (el.closest('[data-faq-body], .pdp-acc__body')) return;
      el.dataset.observed = '1';
      o.observe(el);
    });
  }

  /* ----------------------- Reviews marquee ---------------------------- */
  function initMarquee(scope) {
    scope.querySelectorAll('[data-marquee]').forEach(function (root) {
      if (root.dataset.bound === '1') return;
      root.dataset.bound = '1';

      var track = root.querySelector('.marquee__track');
      if (!track) return;

      // clone the set once for a seamless loop
      Array.prototype.slice.call(track.children).forEach(function (n) {
        track.appendChild(n.cloneNode(true));
      });

      var setW = 0;
      var measure = function () { setW = track.scrollWidth / 2; };
      measure();
      window.addEventListener('resize', measure);

      var offset = 0;
      var speed = 0.45;            // px per frame (auto)
      var dragging = false;
      var startX = 0, startOffset = 0, lastX = 0, velocity = 0;
      var paused = reduce;         // no auto-drift when reduced motion

      function wrap() {
        if (setW <= 0) return;
        while (offset <= -setW) offset += setW;
        while (offset > 0) offset -= setW;
      }

      function frame() {
        if (!dragging) {
          if (!paused) offset -= speed;
          if (Math.abs(velocity) > 0.1) { offset += velocity; velocity *= 0.92; }
          else velocity = 0;
        }
        wrap();
        track.style.transform = 'translate3d(' + offset + 'px,0,0)';
        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);

      if (!reduce) {
        root.addEventListener('mouseenter', function () { if (!dragging) paused = true; });
        root.addEventListener('mouseleave', function () { if (!dragging) paused = false; });
      }

      root.addEventListener('pointerdown', function (e) {
        dragging = true;
        root.classList.add('is-dragging');
        try { root.setPointerCapture(e.pointerId); } catch (err) {}
        startX = e.clientX; startOffset = offset; lastX = e.clientX; velocity = 0;
      });
      root.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        offset = startOffset + (e.clientX - startX);
        velocity = e.clientX - lastX;
        lastX = e.clientX;
      });
      var end = function (e) {
        if (!dragging) return;
        dragging = false;
        root.classList.remove('is-dragging');
        try { root.releasePointerCapture(e.pointerId); } catch (err) {}
        if (!reduce) paused = false;
      };
      root.addEventListener('pointerup', end);
      root.addEventListener('pointercancel', end);
    });
  }

  /* ------------------------- Infographic ------------------------------ */
  function initInfographic(scope) {
    // desktop callout points: hover expands via CSS; click toggles for
    // touch screens + keyboard access. Click-opening one closes the others
    // in the same stage (exclusive).
    scope.querySelectorAll('[data-point]').forEach(function (p) {
      if (p.dataset.bound === '1') return;
      p.dataset.bound = '1';
      var head = p.querySelector('.point__head');
      if (!head) return;
      head.setAttribute('tabindex', '0');
      head.setAttribute('role', 'button');
      head.setAttribute('aria-expanded', 'false');
      var toggle = function () {
        var willOpen = !p.classList.contains('is-open');
        var stage = p.closest('.info__stage') || scope;
        stage.querySelectorAll('[data-point]').forEach(function (other) {
          if (other === p || !other.classList.contains('is-open')) return;
          other.classList.remove('is-open');
          var oh = other.querySelector('.point__head');
          if (oh) oh.setAttribute('aria-expanded', 'false');
        });
        p.classList.toggle('is-open', willOpen);
        head.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      };
      head.addEventListener('click', toggle);
      head.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      });
    });

    // mobile stacked accordion: opening one closes the others (exclusive)
    scope.querySelectorAll('[data-info-item]').forEach(function (item) {
      if (item.dataset.bound === '1') return;
      item.dataset.bound = '1';
      var btn = item.querySelector('button');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var willOpen = !item.classList.contains('is-open');
        var group = item.closest('.info__list') || scope;
        group.querySelectorAll('[data-info-item]').forEach(function (other) {
          if (other === item || !other.classList.contains('is-open')) return;
          other.classList.remove('is-open');
          var ob = other.querySelector('button');
          if (ob) ob.setAttribute('aria-expanded', 'false');
        });
        item.classList.toggle('is-open', willOpen);
        btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
    });
  }

  /* ------------------------- Mobile nav ------------------------------- */
  function initMobileNav(scope) {
    scope.querySelectorAll('[data-nav-toggle]').forEach(function (btn) {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      var id = btn.getAttribute('aria-controls');
      var panel = id ? document.getElementById(id) : null;
      if (!panel) return;
      var close = function () {
        panel.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        document.documentElement.style.removeProperty('overflow');
      };
      btn.addEventListener('click', function () {
        var open = panel.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.documentElement.style.overflow = open ? 'hidden' : '';
      });
      panel.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', close);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close();
      });
    });
  }

  /* ------------------------- PDP gallery ------------------------------ */
  function initGallery(scope) {
    scope.querySelectorAll('[data-gallery]').forEach(function (gal) {
      if (gal.dataset.bound === '1') return;
      gal.dataset.bound = '1';
      var main = gal.querySelector('[data-gallery-main]');
      var thumbs = Array.prototype.slice.call(gal.querySelectorAll('[data-thumb]'));
      if (!main) return;
      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          var full = t.getAttribute('data-full');
          if (!full) return;
          main.style.opacity = '0';
          var swap = function () {
            main.src = full;
            main.style.opacity = '1';
          };
          if (full === main.src) { main.style.opacity = '1'; }
          else { setTimeout(swap, 180); }
          thumbs.forEach(function (o) { o.classList.remove('is-active'); });
          t.classList.add('is-active');
        });
      });
    });
  }

  /* ------------------------- PDP accordions --------------------------- */
  // Exclusive accordion: opening one closes the others in the same group.
  // The body text rises line-by-line (curtain reveal) every time it opens.
  function initAccordions(scope) {
    scope.querySelectorAll('[data-acc]').forEach(function (acc) {
      if (acc.dataset.bound === '1') return;
      acc.dataset.bound = '1';
      var btn = acc.querySelector('.pdp-acc__head');
      if (!btn) return;

      acc._state = { lines: prepCollapse(acc.querySelector('.pdp-acc__body .rte')), split: false };

      // If it ships open by default, reveal once on load.
      if (acc.classList.contains('is-open') && acc._state.lines.length) showCollapse(acc._state);

      btn.addEventListener('click', function () {
        var willOpen = !acc.classList.contains('is-open');

        // close every other open item in the same group (exclusive behaviour)
        var group = acc.closest('.pdp-accordions') || scope;
        group.querySelectorAll('[data-acc]').forEach(function (other) {
          if (other === acc || !other.classList.contains('is-open')) return;
          other.classList.remove('is-open');
          var oh = other.querySelector('.pdp-acc__head');
          if (oh) oh.setAttribute('aria-expanded', 'false');
          if (other._state) hideCollapse(other._state);
        });

        acc.classList.toggle('is-open', willOpen);
        btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (willOpen) showCollapse(acc._state);
        else hideCollapse(acc._state);
      });
    });
  }

  /* ------------------------- FAQ accordion ---------------------------- */
  // Exclusive accordion: opening one item closes the others. Each answer's
  // text rises line-by-line (same "curtain" reveal) every time it opens.
  function initFaq(scope) {
    scope.querySelectorAll('[data-faq]').forEach(function (group) {
      if (group.dataset.bound === '1') return;
      group.dataset.bound = '1';

      var items = Array.prototype.slice.call(group.querySelectorAll('[data-faq-item]'));

      items.forEach(function (item) {
        var head = item.querySelector('.faq-item__head');
        if (!head) return;
        item._state = { lines: prepCollapse(item.querySelector('.faq-item__answer')), split: false };

        head.addEventListener('click', function () {
          var willOpen = !item.classList.contains('is-open');

          // close every other open item (exclusive behaviour)
          items.forEach(function (other) {
            if (other === item || !other.classList.contains('is-open')) return;
            other.classList.remove('is-open');
            var oh = other.querySelector('.faq-item__head');
            if (oh) oh.setAttribute('aria-expanded', 'false');
            hideCollapse(other._state);
          });

          item.classList.toggle('is-open', willOpen);
          head.setAttribute('aria-expanded', willOpen ? 'true' : 'false');

          if (willOpen) showCollapse(item._state);
          else hideCollapse(item._state); // reset so it replays next open
        });
      });
    });
  }


  function initCarousel(scope) {
    scope.querySelectorAll('[data-carousel]').forEach(function (root) {
      if (root.dataset.bound === '1') return;
      root.dataset.bound = '1';

      var track = root.querySelector('[data-track]');
      var slides = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-dot]'));
      var prev = root.querySelector('[data-prev]');
      var next = root.querySelector('[data-next]');
      if (!track || slides.length <= 1) return;

      var index = 0;
      var interval = parseInt(root.getAttribute('data-interval'), 10) || 5000;
      var timer = null;

      function render() {
        track.style.transform = 'translate3d(' + (-index * 100) + '%,0,0)';
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
      }
      function go(i) {
        index = (i + slides.length) % slides.length;
        render();
      }
      function start() {
        if (reduce) return;
        stop();
        timer = setInterval(function () { go(index + 1); }, interval);
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      if (next) next.addEventListener('click', function () { go(index + 1); start(); });
      if (prev) prev.addEventListener('click', function () { go(index - 1); start(); });
      dots.forEach(function (d, i) {
        d.addEventListener('click', function () { go(i); start(); });
      });

      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);

      // basic swipe on touch
      var sx = 0, dragging = false;
      root.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; dragging = true; stop(); }, { passive: true });
      root.addEventListener('touchend', function (e) {
        if (!dragging) return;
        dragging = false;
        var dx = e.changedTouches[0].clientX - sx;
        if (Math.abs(dx) > 40) { go(index + (dx < 0 ? 1 : -1)); }
        start();
      });

      render();
      start();
    });
  }

  /* --------------------- PDP product form / variants ------------------ */
  function initProductForm(scope) {
    scope.querySelectorAll('.pdp-form').forEach(function (form) {
      if (form.dataset.bound === '1') return;
      form.dataset.bound = '1';

      var dataEl = form.querySelector('[data-variant-data]');
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }
      if (!variants || !variants.length) return;

      var selects   = Array.prototype.slice.call(form.querySelectorAll('[data-variant-option]'));
      var idInput   = form.querySelector('[data-variant-id]');
      var buy       = form.querySelector('[data-buy]');
      var buyLabel  = form.querySelector('[data-buy-label]');
      var base      = buy ? (buy.getAttribute('data-buy-base') || '') : '';
      var info      = form.closest('.pdp-info') || scope;
      // Buscamos los precios en toda la sección para incluir la cabecera móvil
      // (.pdp-mhead) además del precio original de la columna de información.
      var priceRoot  = form.closest('.pdp-hero') || info;
      var priceNows  = priceRoot.querySelectorAll('[data-price-now]');
      var priceWass  = priceRoot.querySelectorAll('[data-price-was]');
      // La columna del comparativo (rótulo + precio) se muestra/oculta entera.
      var priceWasCols = priceRoot.querySelectorAll('[data-price-was-col]');
      var priceSaves = priceRoot.querySelectorAll('[data-price-save]');
      // El badge de ahorro lleva un icono + un span de texto; actualizamos solo
      // el texto para no borrar el icono de etiqueta.
      var priceSaveTxts = priceRoot.querySelectorAll('[data-price-save-txt]');

      function match() {
        if (!selects.length) return variants[0];
        var i, j, v, ok;
        for (i = 0; i < variants.length; i++) {
          v = variants[i];
          if (!v.options || v.options.length !== selects.length) continue;
          ok = true;
          for (j = 0; j < selects.length; j++) {
            if (v.options[j] !== selects[j].value) { ok = false; break; }
          }
          if (ok) return v;
        }
        return null;
      }

      function update() {
        var v = match();
        if (!v) {
          if (buy) { buy.disabled = true; if (buyLabel) buyLabel.textContent = 'No disponible'; }
          return;
        }
        if (idInput) idInput.value = v.id;
        priceNows.forEach(function (el) { el.textContent = v.price; });
        priceWass.forEach(function (el) { el.textContent = v.compare_at; });
        priceWasCols.forEach(function (el) { el.hidden = !v.on_sale; });
        priceSaves.forEach(function (el) { el.hidden = !v.on_sale; });
        priceSaveTxts.forEach(function (el) { el.textContent = 'AHORRA ' + v.save_pct + '%'; });
        if (buy) {
          buy.disabled = !v.available;
          if (buyLabel) buyLabel.textContent = v.available ? (base + ' \u00b7 ' + v.price) : 'Agotado';
        }
      }

      selects.forEach(function (s) { s.addEventListener('change', update); });
      update();
    });
  }

  /* ------------------------------ Boot -------------------------------- */
  function bootInteractions(scope) {
    initHeader();
    initMobileNav(scope);
    initMarquee(scope);
    initInfographic(scope);
    initGallery(scope);
    initAccordions(scope);
    initFaq(scope);
    initCarousel(scope);
    initProductForm(scope);
  }

  function runReveals(scope) {
    if (reduce) {
      scope.querySelectorAll('.reveal-lines').forEach(function (el) {
        el.classList.add('is-split', 'is-in');
      });
      return;
    }
    buildReveals(scope);
    observeReveals(scope);
    watchReveals(scope);
  }

  /* ---- Re-partido: cuando cambia el ANCHO REAL del texto ---------------
     ARREGLO (v8.2) · los beneficios del héroe se deformaban en la preview
     de Shopify. El re-partido solo se disparaba al cambiar el ancho de la
     VENTANA, y en el editor la ventana no cambia: lo que cambia es la
     columna, porque al tocar un ajuste Shopify repinta la sección entera y
     la imagen, la fuente o la rejilla se asientan un instante después. El
     primer partido medía una caja que aún no era la definitiva.
     Ahora se vigila el contenedor de cada texto: si su ancho cambia de
     verdad, se vuelve a partir contra la medida buena.
     Se observa el PADRE y no el propio texto a propósito: el padre no
     depende de lo que metamos dentro, así que no puede realimentarse. */
  var revealRO = null, roTimer = null, roQueue = [];
  function flushRO() {
    var list = roQueue; roQueue = [];
    list.forEach(function (host) {
      var kids = host.__revealKids || [];
      kids.forEach(function (el) {
        if (!el.isConnected) return;
        splitLines(el);
        if (el.classList.contains('is-in')) return;
        // Un texto que ya estaba a la vista no debe volver a esconderse.
        if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-in');
      });
    });
  }
  function watchReveals(scope) {
    if (reduce || !window.ResizeObserver) return;
    if (!revealRO) {
      revealRO = new ResizeObserver(function (entries) {
        entries.forEach(function (en) {
          var host = en.target;
          var w = Math.round(en.contentRect.width);
          if (host.__revealHostW === w) return;   // alto solamente: se ignora
          host.__revealHostW = w;
          if (host.__revealQueued) return;
          host.__revealQueued = true;
          roQueue.push(host);
        });
        if (!roQueue.length) return;
        clearTimeout(roTimer);
        roTimer = setTimeout(function () {
          roQueue.forEach(function (h) { h.__revealQueued = false; });
          flushRO();
        }, 120);
      });
    }
    scope.querySelectorAll('.reveal-lines').forEach(function (el) {
      if (el.closest('[data-faq-body], .pdp-acc__body')) return;
      var host = el.parentElement;
      if (!host) return;
      if (!host.__revealKids) {
        host.__revealKids = [];
        host.__revealHostW = Math.round(host.getBoundingClientRect().width);
        revealRO.observe(host);
      }
      if (host.__revealKids.indexOf(el) === -1) host.__revealKids.push(el);
    });
  }

  /* Reintento para los textos que se pidieron partir sin ancho real
     (pestaña cerrada, sección recién inyectada, columna sin asentar). */
  function retryPending(root) {
    (root || document).querySelectorAll('.reveal-lines.is-split').forEach(function (el) {
      if (el.__revealPending) splitLines(el);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    bootInteractions(document);

    if (reduce) { runReveals(document); return; }

    var done = false;
    var go = function () {
      if (done) return;
      done = true;
      runReveals(document);
      // Los textos que aún no tenían ancho (pestañas, columnas sin
      // asentar) se recogen en cuanto la página termina de colocarse.
      [250, 900].forEach(function (t) { setTimeout(function () { retryPending(document); }, t); });
    };
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(go);
      setTimeout(go, 1500); // fallback if fonts hang
    } else {
      go();
    }
  });

  // Re-split on resize ONLY when the viewport WIDTH changes — that is the only
  // thing that changes line wrapping. On mobile, scrolling shows/hides the
  // browser toolbar, which changes viewport HEIGHT and fires 'resize' over and
  // over even though width is unchanged. Re-splitting on those height-only
  // events made the bullet text re-measure repeatedly and slowly drift out of
  // place as the user scrolled ("se desconfigura poco a poco"). Comparing width
  // ignores height-only events, so scrolling no longer triggers a reflow.
  // Rotation and genuine width changes still re-split. Parent keeps .is-in so
  // there is no flash.
  var rt, lastW = window.innerWidth;
  window.addEventListener('resize', function () {
    if (reduce) return;
    if (window.innerWidth === lastW) return; // height-only (mobile chrome): ignore
    lastW = window.innerWidth;
    clearTimeout(rt);
    rt = setTimeout(function () {
      document.querySelectorAll('.reveal-lines.is-split').forEach(splitLines);
    }, 200);
  });

  // Re-split once the page has fully loaded. Reveal targets constrained by a
  // flex/grid sibling (e.g. the guarantee text beside its icon) can be split
  // before an image or web font finishes settling that column's width; the
  // first pass would then group words for a wider box than the final one.
  // Re-running after load measures the settled width, so the grouped lines
  // match the real wrapping. Parent keeps .is-in, so there is no re-flash.
  window.addEventListener('load', function () {
    if (reduce) return;
    document.querySelectorAll('.reveal-lines.is-split').forEach(splitLines);
  });

  // Shopify theme editor: a section was re-rendered — re-wire it.
  //
  // ARREGLO (v8.2) · aquí estaba la otra mitad de "los beneficios se
  // deforman en la preview". En la carga normal el tema espera a
  // `document.fonts.ready` antes de partir el texto; en el editor NO se
  // esperaba a nada. Como Shopify repinta la sección en cada cambio de
  // ajuste, el texto se medía con la tipografía de reserva (métricas
  // distintas a las de Bricolage Grotesque): las palabras se agrupaban
  // para un ancho que luego no era el real y, al entrar la fuente buena,
  // la línea ya no cabía en su máscara `overflow:hidden` y quedaba
  // cortada. Ahora el editor sigue el mismo camiño que la carga normal.
  document.addEventListener('shopify:section:load', function (e) {
    var scope = e.target;
    bootInteractions(scope);

    if (reduce) { runReveals(scope); return; }

    var ran = false;
    var go = function () {
      if (ran || !scope.isConnected) return;
      ran = true;
      runReveals(scope);
      // La sección recién inyectada aún está asentándose (imágenes,
      // rejilla, ancho de columna): se repasa un par de veces.
      [250, 1200].forEach(function (t) {
        setTimeout(function () {
          if (!scope.isConnected) return;
          scope.querySelectorAll('.reveal-lines.is-split').forEach(splitLines);
        }, t);
      });
    };
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(go);
      setTimeout(go, 1200);        // por si la fuente se atasca
    } else {
      go();
    }
  });
})();